import { ComponentProps, ComponentType, ReactNode, useContext, useEffect, useId } from 'react';
import cn from 'classnames';
import { Form } from 'react-bootstrap';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Size } from '@Components/designConstants';
import { Dropdown, SubmenuControlProps, SubmenuSelection } from '@Components/Dropdown';
import { TextInput } from '@Components/TextInput';
import { NO_AUTOFILL_PROPS } from '@src/utils/form-utils';
import { DistributiveOmit } from '@src/utils/type-utils';
import { AnyFormApi, useSelector } from '@tanstack/react-form';
import {
  getFieldErrorMessage,
  revealErrorsContext,
  touchField,
  useFieldContext
} from './form-field-utils';

interface TextFieldProps
  extends
    FieldShellProps,
    Omit<
      ComponentProps<typeof TextInput>,
      | 'id'
      | 'value'
      | 'onChange'
      | 'onBlur'
      | 'onBlurChange'
      | 'label'
      | 'error'
      | 'helper'
      | 'aria-describedby'
    > {}

export const TextField = ({
  label,
  required,
  fieldClassName,
  labelClassName,
  reserveFeedbackSpace,
  ...controlPassthroughProps
}: TextFieldProps): ReactNode => {
  const { field, id, errorId, error, handleTouch, shellProps } = useFieldShell<string>({
    label,
    required,
    fieldClassName,
    labelClassName,
    reserveFeedbackSpace
  });

  return (
    <FormField {...shellProps}>
      <TextInput
        size={Size.MD}
        {...NO_AUTOFILL_PROPS}
        {...controlPassthroughProps}
        id={id}
        name={field.name}
        value={field.state.value}
        onChange={field.handleChange}
        onBlur={handleTouch}
        error={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
      />
    </FormField>
  );
};

type DropdownProps<T extends string> = ComponentProps<typeof Dropdown<T>>;
type FlatDropdownProps<T extends string> = Exclude<DropdownProps<T>, SubmenuControlProps<T>>;
type SingleDropdownProps<T extends string> = Exclude<FlatDropdownProps<T>, { multiSelect: true }>;
type MultiDropdownProps<T extends string> = Extract<FlatDropdownProps<T>, { multiSelect: true }>;
type SubmenuDropdownProps<T extends string> = Extract<DropdownProps<T>, SubmenuControlProps<T>>;

type SingleDropdownFieldProps<T extends string> = FieldShellProps &
  DistributiveOmit<
    SingleDropdownProps<T>,
    'value' | 'onChange' | 'onMenuClose' | 'hasError' | 'label' | 'multiSelect'
  >;

export const SingleDropdownField = <T extends string>({
  label,
  required,
  fieldClassName,
  labelClassName,
  reserveFeedbackSpace,
  ariaLabel,
  clearable = false,
  onClear,
  ...dropdownPassthroughProps
}: SingleDropdownFieldProps<T>): ReactNode => {
  const { field, accessibleName, error, handleTouch, shellProps } = useFieldShell<T | null>({
    label,
    required,
    fieldClassName,
    labelClassName,
    reserveFeedbackSpace
  });

  return (
    <FormField {...shellProps}>
      <Dropdown<T>
        {...dropdownPassthroughProps}
        ariaLabel={ariaLabel ?? accessibleName}
        value={field.state.value ?? undefined}
        onChange={(value: T | null) => field.handleChange(value ?? null)}
        onMenuClose={handleTouch}
        clearable={clearable}
        onClear={() => {
          field.handleChange(null);
          field.handleBlur();
          onClear?.();
        }}
        hasError={Boolean(error)}
      />
    </FormField>
  );
};

type MultiDropdownFieldProps<T extends string> = FieldShellProps &
  Omit<
    MultiDropdownProps<T>,
    'value' | 'onChange' | 'onMenuClose' | 'hasError' | 'label' | 'multiSelect'
  >;

export const MultiDropdownField = <T extends string>({
  label,
  required,
  fieldClassName,
  labelClassName,
  reserveFeedbackSpace,
  ariaLabel,
  ...dropdownPassthroughProps
}: MultiDropdownFieldProps<T>): ReactNode => {
  const { field, accessibleName, error, handleTouch, shellProps } = useFieldShell<T[]>({
    label,
    required,
    fieldClassName,
    labelClassName,
    reserveFeedbackSpace
  });

  return (
    <FormField {...shellProps}>
      <Dropdown<T>
        {...dropdownPassthroughProps}
        multiSelect
        ariaLabel={ariaLabel ?? accessibleName}
        value={field.state.value}
        onChange={values => field.handleChange(values)}
        onMenuClose={handleTouch}
        hasError={Boolean(error)}
      />
    </FormField>
  );
};

type SubmenuDropdownFieldProps<T extends string> = FieldShellProps &
  DistributiveOmit<
    SubmenuDropdownProps<T>,
    'submenuValue' | 'onSubmenuChange' | 'onMenuClose' | 'hasError' | 'label'
  >;

export const SubmenuDropdownField = <T extends string>({
  label,
  required,
  fieldClassName,
  labelClassName,
  reserveFeedbackSpace,
  ariaLabel,
  ...dropdownPassthroughProps
}: SubmenuDropdownFieldProps<T>): ReactNode => {
  const { field, accessibleName, error, handleTouch, shellProps } = useFieldShell<
    SubmenuSelection<T>
  >({
    label,
    required,
    fieldClassName,
    labelClassName,
    reserveFeedbackSpace
  });

  return (
    <FormField {...shellProps}>
      <Dropdown<T>
        {...dropdownPassthroughProps}
        ariaLabel={ariaLabel ?? accessibleName}
        submenuValue={field.state.value}
        onSubmenuChange={selection => field.handleChange(selection)}
        onMenuClose={handleTouch}
        hasError={Boolean(error)}
      />
    </FormField>
  );
};

type SingleDatePickerProps = Extract<
  ComponentProps<typeof DatePicker>,
  { comparison?: false; multiDate?: false }
>;

interface DateFieldProps
  extends
    FieldShellProps,
    Omit<
      SingleDatePickerProps,
      'range' | 'onChange' | 'onClose' | 'isSingle' | 'hasError' | 'label'
    > {}

export const DateField = ({
  label,
  required,
  fieldClassName,
  labelClassName,
  reserveFeedbackSpace,
  ...datePickerPassthroughProps
}: DateFieldProps): ReactNode => {
  const { field, error, handleTouch, shellProps } = useFieldShell<string | null>({
    label,
    required,
    fieldClassName,
    labelClassName,
    reserveFeedbackSpace
  });
  const date = field.state.value;

  // The clear button changes the value without opening the picker, so a change also marks the field touched.
  const handleChange = ({ start }: DateRange) => {
    field.handleChange(start || null);
    field.handleBlur();
  };

  return (
    <FormField {...shellProps}>
      <DatePicker
        {...datePickerPassthroughProps}
        isSingle
        range={date ? { start: date, end: date } : undefined}
        onChange={handleChange}
        onClose={handleTouch}
        hasError={Boolean(error)}
      />
    </FormField>
  );
};

interface AppFieldOfProps<TName extends string, P extends object> {
  // Typed structurally so any useAppForm form fits, and so the form's own AppField still rejects
  // a name that isn't in its values.
  form: { AppField: ComponentType<{ name: TName; children: () => ReactNode }> };
  name: TName;
  // Must read its value from the field context, as the components in this file do.
  field: ComponentType<P>;
  fieldProps: NoInfer<P>;
}

export const AppFieldOf = <TName extends string, P extends object>({
  form,
  name,
  field: Field,
  fieldProps
}: AppFieldOfProps<TName, P>): ReactNode => (
  <form.AppField name={name}>{() => <Field {...fieldProps} />}</form.AppField>
);

interface RevealFieldErrorsProps {
  form: AnyFormApi;
  children: ReactNode;
}

// form-core applies refetched defaults to an untouched form without re-running its validators, so
// validation runs again on each value change until the first touch.
export const RevealFieldErrors = ({ form, children }: RevealFieldErrorsProps): ReactNode => {
  const values = useSelector(form.store, state => state.values);
  const isTouched = useSelector(form.store, state => state.isTouched);
  useEffect(() => {
    if (!isTouched) {
      form.validate('change');
    }
  }, [form, values, isTouched]);
  return <revealErrorsContext.Provider value>{children}</revealErrorsContext.Provider>;
};

interface StaticFieldProps extends Pick<
  FieldShellProps,
  'label' | 'fieldClassName' | 'labelClassName' | 'reserveFeedbackSpace'
> {
  children: ReactNode;
}

// For a read-only input outside form state that should line up with the fields around it.
export const StaticField = ({
  fieldClassName,
  ...formFieldPassthroughProps
}: StaticFieldProps): ReactNode => (
  <FormField {...formFieldPassthroughProps} className={fieldClassName} />
);

interface FormFieldProps {
  id?: string;
  errorId?: string;
  label?: ReactNode;
  required?: boolean;
  error?: string;
  className?: string;
  labelClassName?: string;
  reserveFeedbackSpace?: boolean;
  children: ReactNode;
}

const FormField = ({
  id,
  errorId,
  label,
  required = false,
  error,
  className,
  labelClassName,
  reserveFeedbackSpace = true,
  children
}: FormFieldProps): ReactNode => (
  <Form.Group className={className}>
    {label !== undefined && (
      <Form.Label htmlFor={id} className={labelClassName}>
        {label}
        {required && <span className='required-mark'> *</span>}
      </Form.Label>
    )}
    {children}
    {/* Bootstrap only reveals `.invalid-feedback` right after an `.is-invalid` input,
        which Dropdown and DatePicker don't render. By default the line stays rendered but
        invisible while there's no error, so a message appearing doesn't shift the layout. */}
    {(reserveFeedbackSpace || error) && (
      <Form.Control.Feedback
        id={errorId}
        type='invalid'
        className={cn('d-block', { invisible: !error })}
      >
        {error ?? '\u00A0'}
      </Form.Control.Feedback>
    )}
  </Form.Group>
);

interface FieldShellProps {
  label?: ReactNode;
  required?: boolean;
  fieldClassName?: string;
  labelClassName?: string;
  // Turn off for a field that can never be invalid, so it doesn't keep an empty line below it.
  reserveFeedbackSpace?: boolean;
}

interface FieldShell<TValue> {
  field: ReturnType<typeof useFieldContext<TValue>>;
  id: string;
  errorId: string;
  error: string | undefined;
  accessibleName: string | undefined;
  handleTouch: () => void;
  shellProps: Omit<FormFieldProps, 'children'>;
}

const useFieldShell = <TValue,>({
  label,
  required = false,
  fieldClassName,
  labelClassName,
  reserveFeedbackSpace
}: FieldShellProps): FieldShell<TValue> => {
  const field = useFieldContext<TValue>();
  const id = useId();
  const errorId = `${id}-error`;
  const revealErrors = useContext(revealErrorsContext);
  const error = getFieldErrorMessage(field.state.meta, revealErrors);
  return {
    field,
    id,
    errorId,
    error,
    accessibleName: typeof label === 'string' ? label : undefined,
    handleTouch: () => touchField(field),
    shellProps: {
      id,
      errorId,
      label,
      required,
      error,
      className: fieldClassName,
      labelClassName,
      reserveFeedbackSpace
    }
  };
};
